import { useState, useEffect } from 'react';
import { useRoadmapService, useProjectService} from '../../../../services';
import { formatDate } from '../../../../helpers/data-format';
import {ENTITY, resetDashboardDetails} from "../../../../redux/slices/timeline";
import {useDispatch, useSelector } from 'react-redux';
import {useParams} from "react-router-dom";

interface TaskOption {
  value: string;
  label: string;
  task_start_date?: string;
  task_end_date?: string;
  adj_start_date?: string;
  adj_end_date?: string;
  duration?: number;
  isDisabled?: boolean;
  error?: string;
}

interface TaskDependencyState {
  _uid: string;
  task_id: string;
  task_name: string;
  dependencies: any[];
}

interface UseTaskDependencyManagementProps {
  tasks: any[];
  currentProjectId: string;
  simulation: boolean;
}

export const useTaskDependencyManagement = ({
  tasks,
  currentProjectId,
  simulation
}: UseTaskDependencyManagementProps) => {
  const [taskDependenciesState, setTaskDependenciesState] = useState<TaskDependencyState[]>([]);
  const [editTaskDep, setEditTaskDep] = useState<{depId: string, linkIdx: number} | null>(null);
  const [editTaskDepProject, setEditTaskDepProject] = useState('');
  const [editTaskDepDependsOn, setEditTaskDepDependsOn] = useState<string[]>([]);
  const [isAddingTaskDep, setIsAddingTaskDep] = useState(false);
  const [addTaskDepProject, setAddTaskDepProject] = useState('');
  const [addTaskDepDependsOn, setAddTaskDepDependsOn] = useState<string[]>([]);
  const [isTaskActionLoading, setIsTaskActionLoading] = useState(false);

  const {
    currentEntity,
    currentEntityId
  } = useSelector((state: any) => state.timeline);

  const dispatch = useDispatch();
  const { roadmap_id } = useParams();
  const { fetchData, fetchSingleRoadmap } = useRoadmapService();
  const { updateTaskCompletion } = useProjectService();

  // Initialize task dependencies state from tasks
  useEffect(() => {
    if (!tasks || tasks.length <= 1) {
      setTaskDependenciesState([]);
      return;
    }

    const depMap = new Map();
    tasks.slice(1).forEach((task) => {
      if (Array.isArray(task.dependencies) && task.dependencies.length > 0) {
        // Enhance dependencies with task names and details
        const enhancedDependencies = task.dependencies.map((dep: any) => {
          const dependentTask = tasks.slice(1).find((t: any) => t.id === (dep.task_id || dep.to || dep.id || dep));
          return {
            task_id: dep.task_id || dep.to || dep.id || dep,
            task_name: dependentTask?.task_title || dependentTask?.name || dep.label || 'Unknown Task',
            label: dependentTask?.task_title || dependentTask?.name || dep.label || 'Unknown Task',
            value: dep.task_id || dep.to || dep.id || dep,
            ...dep
          };
        });

        if (!depMap.has(task.id)) {
          depMap.set(task.id, {
            _uid: `${task.id}_${new Date().getTime()}`,
            task_id: task.id,
            task_name: task.task_title || task.name,
            dependencies: enhancedDependencies,
          });
        } else {
          depMap.get(task.id).dependencies.push(...enhancedDependencies);
        }
      }
    });
    setTaskDependenciesState(Array.from(depMap.values()));
  }, [tasks]);

  // Get task list for selection (excluding tasks that already have dependencies)
  const getTaskList = (): TaskOption[] => {
    if (!tasks || tasks.length <= 1) return [];
    
    const optionsArr: TaskOption[] = [];
    tasks.slice(1).forEach((task: any) => {
      // if (
      //   !taskDependenciesState.map(dep => dep.task_id).includes(task.id) ||
      //   task.id === editTaskDepProject
      // ) {
        optionsArr.push({
          value: task.id,
          label: task.task_title || task.name,
          task_start_date: task.adj_start_date || task.start_date,
          task_end_date: task.adj_end_date || task.end_date,
          adj_start_date: task.adj_start_date,
          adj_end_date: task.adj_end_date,
          duration: task.duration,
        });
      // }
    });
    return optionsArr;
  };

  // Circular dependency check removed - users can now create any dependencies

  // Get filtered task list for dependencies (tasks that can be dependencies)
  const getFilterTaskList = (taskId: string): TaskOption[] => {
    if (!tasks || tasks.length <= 1) return [];
    
    const optionsArr: TaskOption[] = [];
    const referenceTask = tasks.slice(1).find((task: any) => task.id === taskId);
    
    if (!referenceTask) return optionsArr;

    // Get task dates - prefer adjusted dates, fall back to start dates, calculate end date
    const getTaskDates = (task: any) => {
      const startDate = task.adj_start_date || task.start_date;
      const duration = task.duration || 1;
      
      if (startDate) {
        const start = new Date(startDate);
        const end = new Date(start);
        end.setDate(end.getDate() + duration);
        return { start, end };
      }
      return { start: null, end: null };
    };

    const { start: refStartDate, end: refEndDate } = getTaskDates(referenceTask);
    
    tasks.slice(1).forEach((task: any) => {
      // Skip if it's the same task (a task cannot depend on itself)
      if (task.id === taskId) return;

      const { start: taskStartDate, end: taskEndDate } = getTaskDates(task);

      // All validation checks removed - allow any task to depend on any other task
      optionsArr.push({
        value: task.id,
        label: task.task_title || task.name,
        task_start_date: task.adj_start_date || task.start_date,
        task_end_date: taskEndDate ? taskEndDate.toISOString().split('T')[0] : undefined,
        adj_start_date: task.adj_start_date,
        adj_end_date: task.adj_end_date,
        duration: task.duration,
        isDisabled: false, // Never disable any options
        error: undefined // No error messages
      });
    });
    
    // No need to sort since all options are enabled
    return optionsArr;
  };

  // Helper function to find task by ID
  const findTaskById = (taskId: string) => {
    if (!tasks || tasks.length <= 1) return null;
    return tasks.slice(1).find((task: any) => task.id === taskId);
  };

  // Helper function to create task update payload
  const createTaskPayload = (taskToUpdate: any, dependencies: string[]) => {
    const payload: any = {
      completion: taskToUpdate.completion || 0,
      description: taskToUpdate.description || "",
      task_title: taskToUpdate.task_title || taskToUpdate.name || "",
      duration: taskToUpdate.duration || 1,
      task_type: taskToUpdate.task_type || "task",
      responsible: taskToUpdate.responsible || "",
      dependencies: dependencies,
      skill_id: taskToUpdate.skill_id ? taskToUpdate.skill_id.toString() : "",
      bid_price: Number(taskToUpdate.bid_price || 0),
      billing_price: Number(taskToUpdate.billing_price || 0),
      additional_resources: taskToUpdate.additional_resources ? taskToUpdate.additional_resources.map((id: any) => id.toString()) : [],
    };

    // Handle delayed_date with same logic as TaskTableModal
    const delayedDate = taskToUpdate.delayed_date || taskToUpdate.start_date;
    if (delayedDate && payload.task_type === 'float') {
      payload['delayed_date'] = formatDate(delayedDate);
    } else {
      payload['delayed_date'] = "";
    }

    // Handle conditional date fields like TaskTableModal
    if (taskToUpdate.task_type === 'float' && taskToUpdate.float_start_date) {
      payload['float_start_date'] = formatDate(taskToUpdate.float_start_date);
    } else if (taskToUpdate.start_date && taskToUpdate.task_type !== 'float') {
      payload['start_date'] = formatDate(taskToUpdate.start_date);
    }
    
    return payload;
  };

  // Update task dependency
  const handleUpdateTaskDependency = async () => {
    if (!editTaskDep || !editTaskDepProject || !currentProjectId) return;
    setIsTaskActionLoading(true);
    try {
      // Find the task being edited
      const taskToUpdate = tasks.slice(1).find((task: any) => task.id === editTaskDepProject);
      if (!taskToUpdate) {
        throw new Error('Task not found');
      }

      // Update local state with enhanced dependencies (similar to project dependency hook)
      const currentDeps = [...taskDependenciesState];
      const updatedDeps = currentDeps.map(dep => {
        if (dep.task_id === editTaskDepProject) {
          return {
            ...dep,
            dependencies: editTaskDepDependsOn.map((id: string) => {
              const dependentTask = tasks.slice(1).find((t: any) => t.id === id);
              return {
                task_id: id,
                task_name: dependentTask?.task_title || dependentTask?.name || 'Unknown Task',
                label: dependentTask?.task_title || dependentTask?.name || 'Unknown Task',
                value: id,
              };
            }),
          };
        }
        return dep;
      }).filter(dep => dep.dependencies.length > 0);

      // Update local state immediately for better UX
      setTaskDependenciesState(updatedDeps);

      // Create payload using helper function
      const UpdateTaskCompletionObject = createTaskPayload(taskToUpdate, editTaskDepDependsOn);

      // Use the same API call as TaskTableModal
      await updateTaskCompletion(currentProjectId, taskToUpdate.item_id || taskToUpdate.id, UpdateTaskCompletionObject);

      setEditTaskDep(null);

      if(currentEntity === ENTITY.PROJECT && currentEntityId) {
        dispatch(resetDashboardDetails());
        fetchData(currentEntityId, simulation, {}, true).then(() => {
          // if(roadmap_id) {
          //   fetchSingleRoadmap(roadmap_id, false)
          // }
        });
      }
    } catch (error) {
      console.error('Error updating task dependency:', error);
    } finally {
      setIsTaskActionLoading(false);
    }
  };

  // Add task dependency
  const handleAddTaskDependency = async () => {
    if (!addTaskDepProject || addTaskDepDependsOn.length === 0 || !currentProjectId) return;
    setIsTaskActionLoading(true);
    try {
      // Find the task being updated
      const taskToUpdate = tasks.slice(1).find((task: any) => task.id === addTaskDepProject);
      if (!taskToUpdate) {
        throw new Error('Task not found');
      }

      // Get existing dependencies and merge with new ones
      const existingDependencies = taskToUpdate.dependencies ? taskToUpdate.dependencies
        .map((dep: any) => {
          const id = dep.task_id || dep.to || dep.id || dep;
          return id ? id.toString() : null;
        })
        .filter((id: string | null) => id !== null) : [];
      const newDependencyStrings = addTaskDepDependsOn.map(id => id.toString());
      const allDependencies = Array.from(new Set([...existingDependencies, ...newDependencyStrings])); // Remove duplicates

      // Update local state with enhanced dependencies (similar to project dependency hook)
      const currentDeps = [...taskDependenciesState];
      const newEnhancedDependencies = addTaskDepDependsOn.map((id: string) => {
        const dependentTask = tasks.slice(1).find((t: any) => t.id === id);
        return {
          task_id: id,
          task_name: dependentTask?.task_title || dependentTask?.name || 'Unknown Task',
          label: dependentTask?.task_title || dependentTask?.name || 'Unknown Task',
          value: id,
        };
      });

      let updatedDeps;
      const existingDepIdx = currentDeps.findIndex(dep => dep.task_id === addTaskDepProject);
      if (existingDepIdx !== -1) {
        currentDeps[existingDepIdx].dependencies = [
          ...currentDeps[existingDepIdx].dependencies,
          ...newEnhancedDependencies
        ];
        updatedDeps = currentDeps;
      } else {
        updatedDeps = [
          ...currentDeps,
          {
            _uid: `${addTaskDepProject}_${new Date().getTime()}`,
            task_id: addTaskDepProject,
            task_name: taskToUpdate.task_title || taskToUpdate.name,
            dependencies: newEnhancedDependencies,
          }
        ];
      }

      // Update local state immediately for better UX
      setTaskDependenciesState(updatedDeps);

      // Create payload using helper function
      const UpdateTaskCompletionObject = createTaskPayload(taskToUpdate, allDependencies);

      // Use the same API call as TaskTableModal
      await updateTaskCompletion(currentProjectId, taskToUpdate.item_id || taskToUpdate.id, UpdateTaskCompletionObject);

      setIsAddingTaskDep(false);
      setAddTaskDepProject('');
      setAddTaskDepDependsOn([]);

      if(currentEntity === ENTITY.PROJECT && currentEntityId) {
        dispatch(resetDashboardDetails());
        fetchData(currentEntityId, simulation, {}, true).then(() => {
          // if(roadmap_id) {
          //   fetchSingleRoadmap(roadmap_id, false)
          // }
        });
      }
      
    } catch (error) {
      console.error('Error adding task dependency:', error);
    } finally {
      setIsTaskActionLoading(false);
    }
  };

  // Delete single task dependency
  const handleDeleteSingleTaskDependency = async (taskId: string, dependencyId: string) => {
    if (!currentProjectId) return;
    setIsTaskActionLoading(true);
    try {
      // Find the task being updated
      const taskToUpdate = tasks.slice(1).find((task: any) => task.id === taskId);
      if (!taskToUpdate) {
        throw new Error('Task not found');
      }

      // Get existing dependencies and remove the specified one
      const existingDependencies = taskToUpdate.dependencies ? taskToUpdate.dependencies
        .map((dep: any) => {
          const id = dep.task_id || dep.to || dep.id || dep;
          return id ? id.toString() : null;
        })
        .filter((id: string | null) => id !== null) : [];
      const updatedDependencies = existingDependencies.filter((depId: string) => depId !== dependencyId.toString());

      // Create payload using helper function
      const UpdateTaskCompletionObject = createTaskPayload(taskToUpdate, updatedDependencies);

      // Use the same API call as TaskTableModal
      await updateTaskCompletion(currentProjectId, taskToUpdate.item_id || taskToUpdate.id, UpdateTaskCompletionObject);

      if(currentEntity === ENTITY.PROJECT && currentEntityId) {
        dispatch(resetDashboardDetails());
        fetchData(currentEntityId, simulation, {}, true).then(() => {
          // if(roadmap_id) {
          //   fetchSingleRoadmap(roadmap_id, false)
          // }
        });
      }
      
    } catch (error) {
      console.error('Error deleting single task dependency:', error);
    } finally {
      setIsTaskActionLoading(false);
    }
  };

  // Delete all dependencies for a task
  const handleDeleteAllTaskDependencies = async (taskId: string) => {
    if (!currentProjectId) return;
    setIsTaskActionLoading(true);
    try {
      // Find the task being updated
      const taskToUpdate = tasks.slice(1).find((task: any) => task.id === taskId);
      if (!taskToUpdate) {
        throw new Error('Task not found');
      }

      // Create payload using helper function with empty dependencies
      const UpdateTaskCompletionObject = createTaskPayload(taskToUpdate, []);

      // Use the same API call as TaskTableModal
      await updateTaskCompletion(currentProjectId, taskToUpdate.item_id || taskToUpdate.id, UpdateTaskCompletionObject);

      if(currentEntity === ENTITY.PROJECT && currentEntityId) {
        dispatch(resetDashboardDetails());
        fetchData(currentEntityId, simulation, {}, true).then(() => {
          // if(roadmap_id) {
          //   fetchSingleRoadmap(roadmap_id, false)
          // }
        });
      }
      
    } catch (error) {
      console.error('Error deleting all task dependencies:', error);
    } finally {
      setIsTaskActionLoading(false);
    }
  };

  return {
    // State
    taskDependenciesState,
    editTaskDep,
    editTaskDepProject,
    editTaskDepDependsOn,
    isAddingTaskDep,
    addTaskDepProject,
    addTaskDepDependsOn,
    isTaskActionLoading,
    
    // Setters
    setEditTaskDep,
    setEditTaskDepProject,
    setEditTaskDepDependsOn,
    setIsAddingTaskDep,
    setAddTaskDepProject,
    setAddTaskDepDependsOn,
    
    // Functions
    getFilterTaskList,
    getTaskList,
    findTaskById,
    handleUpdateTaskDependency,
    handleAddTaskDependency,
    handleDeleteSingleTaskDependency,
    handleDeleteAllTaskDependencies,
  };
}; 